import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "@shared/shared/tickerFilterInput";
import {
  fmtNum,
  fmtUsd,
  invalidateWheelSnapshots,
  loadPositionsSnapshot,
  normalizeTicker,
  sharesAvailable,
  type OptionPosition,
  type PositionsSnapshot,
  type StockPosition,
} from "@shared/ui/watchlistData";
import { scopeAllowsAccount, subscribeAccountScope } from "@shared/ui/accountScope";
import {
  fetchOwnLedgerShareAvgBooks,
  loadOwnLedgerOpenOptions,
  ownLedgerCallPositions,
  type OwnLedgerShareAvgHolding,
} from "@shared/ui/ownLedgerClient";
import { loadTransactionsSnapshot } from "@shared/ui/wheelTransactions";
import { bindDetailsCollapsiblePersistence, collapsibleOpenAttr } from "@shared/ui/collapsibleSections";
import { withPreservedInputFocus } from "@shared/ui/preserveInputFocus";
import {
  buildShareHistoryMeta,
  getShareHistoryEvents,
  renderShareHistoryPanel,
  type ShareHistoryTransaction,
} from "@shared/ui/stockShareHistory";
import {
  displayTableColumnsForData,
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  renderTableHeadRow,
  renderTableColgroup,
  renderTablePinnedAttr,
  type ColumnState,
} from "@shared/ui/tableColumns";
import {
  compactSubline,
  isWebCompactMode,
  resolveWebTableColumns,
} from "@shared/ui/webCompactMode";
import { renderFocusStockRowMenu, STOCK_HISTORY_OPEN_EVENT, syncFocusRowMenuAfterDomUpdate } from "@shared/ui/focusActions";
import { openHistoryTransactionAddModal } from "@shared/ui/historyTransactionAdd";
import { wheelGet } from "./api";
import { paintPanelLoadError } from "./authUi";
import { notifyHeaderFeedQuoteRefreshed } from "@shared/ui/headerFeedIndicator";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  markForAccountId,
  subscribeLedgerMarks,
} from "@shared/ui/ledgerMarks";

const FILTERS_KEY = "sfe-web-stocks-filters-v1";
const TABLE_ID = "web-stocks-positions" as const;
const CACHE_MS = 60_000;
const TXN_CACHE_MS = 180_000;

const DONUT_COLORS = [
  "#ff7a18",
  "#2fbf71",
  "#5b8def",
  "#b07cff",
  "#f5a623",
  "#ef4565",
  "#3dd6d0",
  "#9aa0a6",
  "#e67e22",
  "#7b61ff",
  "#47a6ff",
  "#c9a227",
];

export interface StocksFilterState {
  ticker: string;
  sector: string;
  /** all = every holding; available = shares free of open short calls. */
  shares: "all" | "available";
}

interface EnrichedStockRow {
  stock: StockPosition;
  ticker: string;
  accountId: string;
  sector: string;
  shares: number;
  available: number | null;
  avgCost: number | null;
  adjustedAvg: number | null;
  daysHeld: number | null;
  price: number | null;
  target: number | null;
  costBasis: number | null;
  mktValue: number | null;
  pl: number | null;
  plPct: number | null;
}

interface StocksPanelCache {
  /** SF snapshot used only for enrichment (sector, target, available). */
  positions: PositionsSnapshot;
  rows: EnrichedStockRow[];
  /** Why own-ledger holdings could not be read, when they could not be. */
  loadError: string | null;
  fetchedAt: number;
}

const EMPTY_POSITIONS: PositionsSnapshot = { options: [], stock: [], fetchedAt: 0 };

/** Columns whose displayed values are enriched from Stock Forge. */
const SF_ENRICHED_COL_KEYS = new Set(["target"]);

interface DiversitySlice {
  label: string;
  value: number;
  color: string;
  pct: number;
}

let panelCache: StocksPanelCache | null = null;
let panelInflight: Promise<StocksPanelCache> | null = null;
let mountedPanel: HTMLElement | null = null;
let stocksRerender: (() => void) | null = null;
subscribeAccountScope(() => stocksRerender?.());
subscribeLedgerMarks(() => {
  stocksRerender?.();
});
let expandedTicker: string | null = null;
let shareHistoryTxns: ShareHistoryTransaction[] | null = null;
let shareHistoryTxnsAt = 0;

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function asNum(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function defaultFilters(): StocksFilterState {
  return { ticker: "", sector: "", shares: "all" };
}

function loadFilters(): StocksFilterState {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return defaultFilters();
    const parsed = { ...defaultFilters(), ...(JSON.parse(raw) as Partial<StocksFilterState>) };
    parsed.shares = parsed.shares === "available" ? "available" : "all";
    parsed.ticker = sanitizeTickerFilterValue(parsed.ticker).toUpperCase();
    return parsed;
  } catch {
    return defaultFilters();
  }
}

function saveFilters(state: StocksFilterState): void {
  localStorage.setItem(FILTERS_KEY, JSON.stringify(state));
}

function sfStockByTicker(positions: PositionsSnapshot): Map<string, StockPosition> {
  const map = new Map<string, StockPosition>();
  for (const stock of positions.stock) {
    const ticker = normalizeTicker(stock.ticker);
    if (ticker) map.set(ticker, stock);
  }
  return map;
}

function optionsForAccountTicker(
  options: OptionPosition[],
  ticker: string,
  accountId: string,
): OptionPosition[] {
  const key = normalizeTicker(ticker);
  const tickerOpts = options.filter((o) => normalizeTicker(o.ticker) === key);
  const account = String(accountId || "").trim();
  if (!account) return tickerOpts;
  const same = tickerOpts.filter((o) => String(o.account_id || "").trim() === account);
  return same.length ? same : tickerOpts.filter((o) => !o.account_id);
}

function holdingRowKey(accountId: string, ticker: string): string {
  return `${accountId || "_"}|${normalizeTicker(ticker)}`;
}

/**
 * Row universe = own-ledger open holdings, one row per account + ticker.
 * Marks + sector from Robinhood MCP. Available uses same-account open calls.
 */
function enrichRows(
  ownBooks: OwnLedgerShareAvgHolding[],
  positions: PositionsSnapshot,
  openCalls: OptionPosition[],
  priceByTicker: Map<string, number>,
  sectorByTicker: Map<string, string>,
): EnrichedStockRow[] {
  const sfByTicker = sfStockByTicker(positions);
  const rows: EnrichedStockRow[] = [];

  for (const own of [...ownBooks].sort(
    (a, b) => a.ticker.localeCompare(b.ticker) || a.accountId.localeCompare(b.accountId),
  )) {
    const ticker = normalizeTicker(own.ticker);
    const shares = Math.max(0, Number(own.quantity) || 0);
    if (!ticker || !(shares > 0)) continue;

    const sf = sfByTicker.get(ticker);
    const options = optionsForAccountTicker(openCalls, ticker, own.accountId);
    const avgCost = own.avgCost > 0 ? own.avgCost : null;
    const adjustedAvg = own.adjustedAvg > 0 ? own.adjustedAvg : null;
    const costBasis =
      own.costBasis > 0 ? own.costBasis : avgCost != null ? avgCost * shares : null;
    const rhPrice = priceByTicker.get(ticker);
    const price = rhPrice != null && rhPrice > 0 ? rhPrice : null;
    const mktValue = price != null ? price * shares : null;
    const pl = mktValue != null && costBasis != null ? mktValue - costBasis : null;
    const plPct = pl != null && costBasis != null && costBasis > 0 ? (pl / costBasis) * 100 : null;
    const daysHeld = own.daysHeld != null && own.daysHeld >= 0 ? own.daysHeld : null;
    const sector = sectorByTicker.get(ticker)?.trim() || sf?.sector?.trim() || "Unknown";

    const stock: StockPosition = {
      ticker,
      quantity: shares,
      avg_price: avgCost ?? undefined,
      cost_basis: costBasis ?? undefined,
      current_price: price ?? undefined,
      current_pl: pl ?? undefined,
      current_pl_pct: plPct ?? undefined,
      sector,
      target_price: sf?.target_price,
      days_held: daysHeld ?? undefined,
    };

    rows.push({
      stock,
      ticker,
      accountId: own.accountId,
      sector,
      shares,
      available: sharesAvailable(stock, options),
      avgCost,
      adjustedAvg,
      daysHeld,
      price,
      target: asNum(sf?.target_price),
      costBasis,
      mktValue,
      pl,
      plPct,
    });
  }

  return rows;
}

function applyFilters(rows: EnrichedStockRow[], state: StocksFilterState): EnrichedStockRow[] {
  const q = state.ticker.trim().toUpperCase();
  return rows.filter((row) => {
    if (!scopeAllowsAccount(row.accountId)) return false;
    if (q && !row.ticker.startsWith(q)) return false;
    if (state.sector && row.sector !== state.sector) return false;
    if (state.shares === "available" && !(row.available != null && row.available > 0)) {
      return false;
    }
    return true;
  });
}

function fmtMoney(value: number | null | undefined, signed = false): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const text = fmtUsd(Math.abs(value));
  if (!signed || value === 0) return value < 0 ? `-${text}` : text;
  return value < 0 ? `-${text}` : text;
}

function fmtPctSigned(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const pct = Math.abs(value) <= 1 && Math.abs(value) !== 1 ? value * 100 : value;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

function plClass(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value === 0) return "";
  return value > 0 ? "roi-strong" : "roi-loss";
}

function computeSummary(rows: EnrichedStockRow[]): {
  positions: number;
  shares: number;
  costBasis: number;
  marketValue: number;
  totalPl: number;
  totalPlPct: number | null;
} {
  let shares = 0;
  let costBasis = 0;
  let marketValueTotal = 0;
  let totalPl = 0;

  for (const row of rows) {
    shares += row.shares;
    if (row.costBasis != null) costBasis += row.costBasis;
    if (row.mktValue != null) marketValueTotal += row.mktValue;
    if (row.pl != null) totalPl += row.pl;
  }

  const totalPlPct =
    costBasis > 0 && Number.isFinite(totalPl) ? (totalPl / costBasis) * 100 : null;

  return {
    positions: rows.length,
    shares,
    costBasis,
    marketValue: marketValueTotal,
    totalPl,
    totalPlPct,
  };
}

function buildDiversitySlices(
  rows: EnrichedStockRow[],
  key: "sector" | "ticker",
): DiversitySlice[] {
  const totals = new Map<string, number>();
  let grand = 0;
  for (const row of rows) {
    const mv = row.mktValue ?? 0;
    if (mv <= 0) continue;
    const label = key === "sector" ? row.sector : row.ticker;
    totals.set(label, (totals.get(label) ?? 0) + mv);
    grand += mv;
  }
  if (grand <= 0) return [];
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], i) => ({
      label,
      value,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
      pct: (value / grand) * 100,
    }));
}

function renderDonut(slices: DiversitySlice[], centerTop: string, centerSub: string): string {
  if (!slices.length) {
    return `<p class="div-empty muted">No market value to chart.</p>`;
  }
  const cx = 100;
  const cy = 100;
  const r = 70;
  const stroke = 26;
  const circumference = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  const circles = slices
    .map((slice) => {
      const len = (slice.value / total) * circumference;
      const dash = `${len} ${circumference - len}`;
      const circle = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${slice.color}" stroke-width="${stroke}" stroke-dasharray="${dash}" stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cy})"></circle>`;
      offset += len;
      return circle;
    })
    .join("");
  return `
    <div class="donut-wrap">
      <svg class="donut" viewBox="0 0 200 200" role="img" aria-label="${escAttr(centerSub)}">
        ${circles}
        <text class="donut-center-top" x="${cx}" y="${cy - 2}" text-anchor="middle">${esc(centerTop)}</text>
        <text class="donut-center-sub" x="${cx}" y="${cy + 14}" text-anchor="middle">${esc(centerSub)}</text>
      </svg>
    </div>`;
}

function renderLegend(slices: DiversitySlice[], compact = false): string {
  if (!slices.length) return "";
  const cls = compact ? "div-legend div-legend-cols" : "div-legend";
  const items = slices
    .map(
      (slice) =>
        `<li><span class="sw" style="background:${slice.color}"></span><span class="nm">${esc(slice.label)}</span>${
          compact ? "" : `<span class="vl">${fmtMoney(slice.value)}</span>`
        }<span class="pc">${slice.pct.toFixed(1)}%</span></li>`,
    )
    .join("");
  return `<ul class="${cls}">${items}</ul>`;
}

function sectorOptions(rows: EnrichedStockRow[], selected: string): string {
  const sectors = new Set(rows.map((r) => r.sector));
  const opts = [`<option value="">All sectors</option>`];
  for (const sector of [...sectors].sort()) {
    const sel = sector === selected ? " selected" : "";
    opts.push(`<option value="${escAttr(sector)}"${sel}>${esc(sector)}</option>`);
  }
  return opts.join("");
}

function renderKpiRow(summary: ReturnType<typeof computeSummary>): string {
  const plCls = plClass(summary.totalPl);
  return `
    <div class="kpi-row">
      <div class="kpi"><span class="kpi-k">Stock positions</span><span class="kpi-v">${summary.positions}</span></div>
      <div class="kpi"><span class="kpi-k">Shares</span><span class="kpi-v">${fmtNum(summary.shares, 0)}</span></div>
      <div class="kpi"><span class="kpi-k">Cost basis</span><span class="kpi-v">${fmtMoney(summary.costBasis)}</span></div>
      <div class="kpi"><span class="kpi-k">Market value</span><span class="kpi-v">${fmtMoney(summary.marketValue)}</span></div>
      <div class="kpi"><span class="kpi-k">Total P/L</span><span class="kpi-v ${plCls}">${fmtMoney(summary.totalPl, true)}</span><span class="kpi-sub ${plCls}">${fmtPctSigned(summary.totalPlPct)}</span></div>
    </div>`;
}

function renderFilterBar(state: StocksFilterState, rows: EnrichedStockRow[]): string {
  return `
    <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="wheelStockTicker">Ticker</label>
        <input type="text" id="wheelStockTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label for="wheelStockSectorFilter">Sector</label>
        <select id="wheelStockSectorFilter" aria-label="Sector filter">${sectorOptions(rows, state.sector)}</select>
      </div>
      <div class="filter-field sfe-stocks-shares-filter" id="sfe-stocks-shares-filter">
        <label>Shares<span class="hint" tabindex="0" role="img" data-tip="Available shows holdings with shares not tied to open short call contracts (available &gt; 0).">i</span></label>
        <div class="year-chips" role="group" aria-label="Filter stocks by available share capacity">
          <button type="button" class="year-chip${state.shares === "all" ? " active" : ""}" data-stocks-shares="all" aria-pressed="${state.shares === "all" ? "true" : "false"}">All</button>
          <button type="button" class="year-chip${state.shares === "available" ? " active" : ""}" data-stocks-shares="available" aria-pressed="${state.shares === "available" ? "true" : "false"}">Available</button>
        </div>
      </div>
      <div class="filter-field">
        <div class="sfe-filter-reset-actions">
          <button type="button" class="small sfe-reset-filters" id="wheelStockResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
        </div>
      </div>
    </div>`;
}

function renderStockCell(colId: string, row: EnrichedStockRow): string {
  const plCls = plClass(row.pl);
  const availCls = row.available != null && row.available > 0 ? "roi-strong" : "";
  const compact = isWebCompactMode();
  switch (colId) {
    case "ticker":
      return `<td class="tk-cell" data-col="ticker"><span class="recs-ticker" data-ticker="${escAttr(row.ticker)}" title="Open ${escAttr(row.ticker)} on Stock Forge">${esc(row.ticker)}<span class="tk-ic">↗</span></span></td>`;
    case "broker_mark":
      return brokerMarkCellHtml(markForAccountId(row.accountId));
    case "account_short":
      return accountShortCellHtml(markForAccountId(row.accountId));
    case "sector":
      return `<td class="sector-cell" data-col="sector">${esc(row.sector)}</td>`;
    case "quantity": {
      const availLabel = row.available != null ? fmtNum(row.available, 0) : "—";
      const cellCls = compact ? " sfe-web-compact-cell" : " sfe-screener-shares-cell";
      return `<td class="num${cellCls}" data-col="quantity">${fmtNum(row.shares, 0)}${compactSubline(`avail ${availLabel}`, availCls || "sfe-web-compact-sub")}</td>`;
    }
    case "sfe_shares_available": {
      const availInner = row.available != null ? fmtNum(row.available, 0) : "—";
      const availSubCls = row.available != null && row.available > 0 ? "roi-strong" : "";
      return `<td class="num sfe-stocks-available-cell" data-col="sfe_shares_available"><span class="${availSubCls}">${availInner}</span></td>`;
    }
    case "avg_price": {
      if (compact) {
        const days = row.daysHeld != null ? `${row.daysHeld} days` : "— days";
        return `<td class="num sfe-web-compact-cell" data-col="avg_price">${fmtUsd(row.avgCost)}${compactSubline(days, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="avg_price">${fmtUsd(row.avgCost)}</td>`;
    }
    case "adjusted_avg":
      return `<td class="num" data-col="adjusted_avg">${fmtUsd(row.adjustedAvg)}</td>`;
    case "days_held":
      return `<td class="num" data-col="days_held">${row.daysHeld != null ? row.daysHeld : "—"}</td>`;
    case "price": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="price">${fmtUsd(row.price)}${compactSubline(`tgt ${fmtUsd(row.target)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="price">${fmtUsd(row.price)}</td>`;
    }
    case "target":
      return `<td class="num" data-col="target">${fmtUsd(row.target)}</td>`;
    case "cost_basis": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="cost_basis">${fmtMoney(row.costBasis)}${compactSubline(`mkt ${fmtMoney(row.mktValue)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="cost_basis">${fmtMoney(row.costBasis)}</td>`;
    }
    case "market_value":
      return `<td class="num" data-col="market_value">${fmtMoney(row.mktValue)}</td>`;
    case "pl_dollar": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell ${plCls}" data-col="pl_dollar">${fmtMoney(row.pl, true)}${compactSubline(fmtPctSigned(row.plPct), plCls || "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num ${plCls}" data-col="pl_dollar">${fmtMoney(row.pl, true)}</td>`;
    }
    case "pl_pct":
      return `<td class="num ${plCls}" data-col="pl_pct">${fmtPctSigned(row.plPct)}</td>`;
    case "actions":
      return `<td class="pos-actions" data-col="actions">${renderFocusStockRowMenu(row.ticker)}</td>`;
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

function stockColumnValue(row: EnrichedStockRow, key: string): unknown {
  switch (key) {
    case "ticker":
      return row.ticker;
    case "broker_mark":
      return markForAccountId(row.accountId)?.brokerCode || null;
    case "account_short":
      return markForAccountId(row.accountId)?.accountShort || null;
    case "sector":
      return row.sector;
    case "quantity":
      return row.shares;
    case "sfe_shares_available":
      return row.available;
    case "avg_price":
      return row.avgCost;
    case "adjusted_avg":
      return row.adjustedAvg;
    case "days_held":
      return row.daysHeld;
    case "price":
      return row.price;
    case "target":
      return row.target;
    case "cost_basis":
      return row.costBasis;
    case "market_value":
      return row.mktValue;
    case "pl_dollar":
      return row.pl;
    case "pl_pct":
      return row.plPct;
    default:
      return null;
  }
}

function visibleColumnsForRows(columns: ColumnState[], rows: EnrichedStockRow[]): ColumnState[] {
  let visible = displayTableColumnsForData(columns, isWebCompactMode(), rows, stockColumnValue);
  if (!isWebCompactMode()) {
    visible = visible.filter((column) => column.key !== "sfe_shares_available");
  }
  return visible;
}

function renderTable(
  rows: EnrichedStockRow[],
  columns: ColumnState[],
  expandedTicker: string | null,
  shareHistoryReady: boolean,
  totalRows = rows.length,
  loadError: string | null = null,
): string {
  const visible = visibleColumnsForRows(columns, rows);
  if (!rows.length) {
    // A failed request, an empty ledger, and an over-restrictive filter each
    // leave the table blank for completely different reasons, so say which.
    const message = loadError
      ? `Could not load own-ledger holdings: ${esc(loadError)}`
      : totalRows > 0
        ? `No stock positions match your filters (0 of ${totalRows} holdings).`
        : "No open share holdings in own ledger.";
    return `<tr><td colspan="${Math.max(visible.length, 1)}" class="muted">${message}</td></tr>`;
  }
  return rows
    .map((row) => {
      const cells = visible.map((column) => renderStockCell(column.key, row)).join("");
      const rowKey = holdingRowKey(row.accountId, row.ticker);
      const expanded = expandedTicker === rowKey;
      const rowCls = expanded ? " sfe-stocks-row-expanded" : "";
      let html = `<tr data-ticker="${escAttr(row.ticker)}" data-account-id="${escAttr(row.accountId)}" data-holding-key="${escAttr(rowKey)}" class="sfe-stocks-row sfe-stocks-row-clickable${rowCls}" tabindex="0" role="button" aria-expanded="${expanded ? "true" : "false"}">${cells}</tr>`;
      if (expanded) {
        const events = shareHistoryReady
          ? getShareHistoryEvents(row.ticker, shareHistoryTxns ?? [], row.accountId)
          : [];
        const meta = buildShareHistoryMeta(
          row.ticker,
          shareHistoryTxns ?? [],
          row.shares,
          row.avgCost,
          row.accountId,
        );
        html += `<tr class="sfe-stocks-share-history-row"><td colspan="${visible.length}">${renderShareHistoryPanel(meta, events, !shareHistoryReady)}</td></tr>`;
      }
      return html;
    })
    .join("");
}

function panelHasDisplayedContent(panel: HTMLElement): boolean {
  return panel.querySelector(".sfe-web-native-stocks") != null;
}

function morphTableCell(td: HTMLTableCellElement, nextHtml: string): boolean {
  const tpl = document.createElement("template");
  tpl.innerHTML = nextHtml.trim();
  const next = tpl.content.firstElementChild;
  if (!(next instanceof HTMLTableCellElement)) return false;
  if (td.outerHTML === next.outerHTML) return false;

  for (const attr of [...td.attributes]) {
    if (!next.hasAttribute(attr.name)) td.removeAttribute(attr.name);
  }
  for (const attr of next.attributes) {
    td.setAttribute(attr.name, attr.value);
  }
  td.innerHTML = next.innerHTML;
  return true;
}

function patchStockRow(tr: HTMLTableRowElement, row: EnrichedStockRow, visible: ColumnState[]): void {
  for (const column of visible) {
    if (column.key === "actions" || column.key === "ticker") continue;
    const td = tr.querySelector<HTMLTableCellElement>(`td[data-col="${column.key}"]`);
    if (!td) continue;
    morphTableCell(td, renderStockCell(column.key, row));
  }
}

function patchStocksTableBody(
  tbody: HTMLElement,
  rows: EnrichedStockRow[],
  columns: ColumnState[],
  shareHistoryReady: boolean,
  totalRows: number,
  loadError: string | null,
): boolean {
  const visible = visibleColumnsForRows(columns, rows);
  if (!rows.length) {
    tbody.innerHTML = renderTable(rows, columns, expandedTicker, shareHistoryReady, totalRows, loadError);
    return true;
  }

  const existingRows = [...tbody.querySelectorAll<HTMLTableRowElement>("tr.sfe-stocks-row[data-holding-key], tr.sfe-stocks-row[data-ticker]")];
  const canPatchInline =
    existingRows.length === rows.length &&
    existingRows.every(
      (tr, index) =>
        (tr.dataset.holdingKey ?? holdingRowKey(tr.dataset.accountId ?? "", tr.dataset.ticker ?? "")) ===
        holdingRowKey(rows[index]!.accountId, rows[index]!.ticker),
    );

  if (!canPatchInline) return false;

  existingRows.forEach((tr, index) => {
    patchStockRow(tr, rows[index]!, visible);
  });
  return true;
}

function patchKpiRow(kpiRow: HTMLElement, summary: ReturnType<typeof computeSummary>): void {
  const cards = [...kpiRow.querySelectorAll<HTMLElement>(".kpi")];
  const plCls = plClass(summary.totalPl);
  const patches: Array<{ value: string; valueCls?: string; sub?: string; subCls?: string }> = [
    { value: String(summary.positions) },
    { value: fmtNum(summary.shares, 0) },
    { value: fmtMoney(summary.costBasis) },
    { value: fmtMoney(summary.marketValue) },
    {
      value: fmtMoney(summary.totalPl, true),
      valueCls: plCls,
      sub: fmtPctSigned(summary.totalPlPct),
      subCls: plCls,
    },
  ];

  cards.forEach((card, index) => {
    const patch = patches[index];
    if (!patch) return;
    const valueEl = card.querySelector<HTMLElement>(".kpi-v");
    if (valueEl) {
      const valueChanged = valueEl.textContent !== patch.value;
      if (valueChanged) valueEl.textContent = patch.value;
      const nextCls = `kpi-v${patch.valueCls ? ` ${patch.valueCls}` : ""}`.trim();
      if (valueEl.className !== nextCls) valueEl.className = nextCls;
    }
    const subEl = card.querySelector<HTMLElement>(".kpi-sub");
    if (patch.sub) {
      if (subEl) {
        if (subEl.textContent !== patch.sub) subEl.textContent = patch.sub;
        const nextSubCls = `kpi-sub${patch.subCls ? ` ${patch.subCls}` : ""}`.trim();
        if (subEl.className !== nextSubCls) subEl.className = nextSubCls;
      }
    }
  });
}

function softUpdatePanel(panel: HTMLElement, state: StocksFilterState, cache: StocksPanelCache): void {
  panelCache = cache;
  notifyHeaderFeedQuoteRefreshed(cache.fetchedAt);
  const columns = resolveWebTableColumns(TABLE_ID);
  const filtered = applyFilters(cache.rows, state);
  const summary = computeSummary(cache.rows);
  const shareHistoryReady = Boolean(
    shareHistoryTxns && Date.now() - shareHistoryTxnsAt < TXN_CACHE_MS,
  );

  const tbody = panel.querySelector<HTMLElement>("#wheelStockBody");
  if (tbody && !patchStocksTableBody(
    tbody,
    filtered,
    columns,
    shareHistoryReady,
    cache.rows.length,
    cache.loadError,
  )) {
    tbody.innerHTML = renderTable(
      filtered,
      columns,
      expandedTicker,
      shareHistoryReady,
      cache.rows.length,
      cache.loadError,
    );
    syncFocusRowMenuAfterDomUpdate();
  }

  const kpiRow = panel.querySelector<HTMLElement>(".kpi-row");
  if (kpiRow) patchKpiRow(kpiRow, summary);

  const note = panel.querySelector<HTMLElement>(".recs-filter-note");
  if (note) {
    note.textContent = `${filtered.length} position${filtered.length === 1 ? "" : "s"}`;
  }

}

function renderPanelShell(
  state: StocksFilterState,
  cache: StocksPanelCache,
  filtered: EnrichedStockRow[],
  columns: ColumnState[],
  expandedTicker: string | null,
  shareHistoryReady: boolean,
  stale = false,
): string {
  const summary = computeSummary(cache.rows);
  const sectorSlices = buildDiversitySlices(cache.rows, "sector");
  const tickerSlices = buildDiversitySlices(cache.rows, "ticker");
  const mv = summary.marketValue;
  const centerTop = mv >= 1000 ? `$${(mv / 1000).toFixed(1)}k` : fmtMoney(mv);
  const staleNote = stale ? " · showing cached data, refreshing…" : "";
  const visibleColumns = visibleColumnsForRows(columns, filtered);

  return `
    <div data-wheel-view="stocks" class="panel sfe-web-native-stocks" style="margin-top:12px;">
      <details class="collapsible" data-sfe-collapsible="positions"${collapsibleOpenAttr("web-stocks", "positions")}>
        <summary><h2 class="insight-h">Stocks · long positions &amp; performance</h2></summary>
        ${renderKpiRow(summary)}
        <p class="muted small">Holdings from own ledger; marks &amp; sector from Robinhood. Orange-dot columns are enriched from Stock Forge when available.${staleNote}</p>
      </details>

      <details class="collapsible" data-sfe-collapsible="diversification"${collapsibleOpenAttr("web-stocks", "diversification")}>
        <summary><h2 class="insight-h">Diversification — share of market value by sector &amp; ticker</h2></summary>
        <div class="diversity-grid">
          <div class="diversity-card">
            <h5>By sector</h5>
            ${renderDonut(sectorSlices, centerTop, "market value")}
            ${renderLegend(sectorSlices)}
          </div>
          <div class="diversity-card">
            <h5>By ticker</h5>
            ${renderDonut(tickerSlices, centerTop, `${tickerSlices.length} tickers`)}
            ${renderLegend(tickerSlices, true)}
          </div>
        </div>
      </details>

      <div class="section-head" style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <h2 class="insight-h" style="margin:0;">Stock positions</h2>
        <button type="button" class="small wheel-add-btn" id="wheelStockAddTxn">+ Add transaction</button>
      </div>
      ${renderFilterBar(state, cache.rows)}
      <p class="muted small recs-filter-note">${filtered.length} position${filtered.length === 1 ? "" : "s"}</p>
      <div class="recs-wrap">
        <div class="recs-hscroll">
          <table class="recs recs-compact recs-sticky-col sfe-wheel-table-stocks" id="wheelStockTable"${renderTablePinnedAttr(visibleColumns)}>
            ${renderTableColgroup(visibleColumns)}
            <thead>
              <tr>${renderStocksTableHeadRow(visibleColumns)}</tr>
            </thead>
            <tbody id="wheelStockBody">${renderTable(filtered, columns, expandedTicker, shareHistoryReady, cache.rows.length, cache.loadError)}</tbody>
          </table>
        </div>
      </div>
    </div>`;
}

function bindFilters(root: HTMLElement, state: StocksFilterState, rerender: () => void): void {
  root.querySelector<HTMLInputElement>("#wheelStockTicker")?.addEventListener("input", (event) => {
    state.ticker = sanitizeTickerFilterValue((event.target as HTMLInputElement).value).toUpperCase();
    saveFilters(state);
    rerender();
  });

  guardTickerFilterInputs(root);

  root.querySelector<HTMLSelectElement>("#wheelStockSectorFilter")?.addEventListener("change", (event) => {
    state.sector = (event.target as HTMLSelectElement).value;
    saveFilters(state);
    rerender();
  });

  root.querySelectorAll<HTMLButtonElement>("[data-stocks-shares]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      state.shares = button.dataset.stocksShares === "available" ? "available" : "all";
      saveFilters(state);
      rerender();
    });
  });

  root.querySelector("#wheelStockResetFilters")?.addEventListener("click", () => {
    Object.assign(state, defaultFilters());
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelStockAddTxn")?.addEventListener("click", () => {
    openHistoryTransactionAddModal({
      defaults: {
        instrument: "stock",
        kind: "stock_buy",
        ticker: state.ticker.trim().toUpperCase() || undefined,
      },
      onSaved: async () => {
        if (mountedPanel) {
          await refreshNativeStocksPanelInBackground(mountedPanel);
        }
      },
    });
  });
}

function markSfEnrichedHeader(headerHtml: string): string {
  if (/sfe-col-sf-enrich/.test(headerHtml)) return headerHtml;
  return headerHtml.replace(/<th\b([^>]*)>/i, (_match, attrs: string) => {
    const titled = /title=/.test(attrs)
      ? attrs
      : `${attrs} title="Enriched from Stock Forge"`;
    if (/class="([^"]*)"/.test(titled)) {
      return `<th${titled.replace(/class="([^"]*)"/, (_m, cls: string) =>
        cls.includes("sfe-col-sf-enrich") ? `class="${cls}"` : `class="${cls} sfe-col-sf-enrich"`,
      )}>`;
    }
    return `<th class="sfe-col-sf-enrich"${titled}>`;
  });
}

function renderStocksTableHeadRow(columns: ColumnState[]): string {
  const marked = columns.map((column) => {
    if (!SF_ENRICHED_COL_KEYS.has(column.key) || !column.headerHtml) return column;
    return { ...column, headerHtml: markSfEnrichedHeader(column.headerHtml) };
  });
  return renderTableHeadRow(marked);
}

async function loadPanelCache(force: boolean): Promise<StocksPanelCache> {
  if (!force && panelCache && Date.now() - panelCache.fetchedAt < CACHE_MS) {
    return panelCache;
  }
  if (panelInflight && !force) return panelInflight;

  const load = async (): Promise<StocksPanelCache> => {
    try {
      let loadError: string | null = null;
      const [ownBooks, positions, openCalls] = await Promise.all([
        fetchOwnLedgerShareAvgBooks(force).catch((err: unknown) => {
          loadError = err instanceof Error ? err.message : String(err);
          return [] as OwnLedgerShareAvgHolding[];
        }),
        loadPositionsSnapshot(force).catch(() => EMPTY_POSITIONS),
        loadOwnLedgerOpenOptions(force)
          .then((rows) => ownLedgerCallPositions(rows))
          .catch(() => [] as OptionPosition[]),
        ensureLedgerMarkIndex(force).catch(() => null),
      ]);
      const tickers = [...new Set(ownBooks.map((book) => book.ticker))];
      const { fetchAgenticEquityQuotes, fetchAgenticEquitySectors } = await import(
        "@shared/api/agenticClient"
      );
      const [priceByTicker, sectorByTicker] = await Promise.all([
        fetchAgenticEquityQuotes(tickers).catch(() => new Map<string, number>()),
        fetchAgenticEquitySectors(tickers).catch(() => new Map<string, string>()),
      ]);
      const cache: StocksPanelCache = {
        positions,
        rows: enrichRows(ownBooks, positions, openCalls, priceByTicker, sectorByTicker),
        loadError,
        fetchedAt: Date.now(),
      };
      panelCache = cache;
      return cache;
    } catch (err) {
      if (panelCache) return panelCache;
      throw err;
    } finally {
      panelInflight = null;
    }
  };

  panelInflight = load();
  return panelInflight;
}

async function loadShareHistoryTransactions(force = false): Promise<ShareHistoryTransaction[]> {
  shareHistoryTxns = await loadTransactionsSnapshot(wheelGet, force, "own_ledger");
  shareHistoryTxnsAt = Date.now();
  return shareHistoryTxns;
}

function bindShareHistory(
  panel: HTMLElement,
  getExpanded: () => string | null,
  setExpanded: (ticker: string | null) => void,
  rerender: () => void,
): void {
  if (panel.dataset.sfeShareHistoryBound === "1") return;
  panel.dataset.sfeShareHistoryBound = "1";

  const toggleHolding = (holdingKey: string): void => {
    const next = getExpanded() === holdingKey ? null : holdingKey;
    setExpanded(next);
    rerender();
    if (next) {
      void loadShareHistoryTransactions().then(() => {
        if (getExpanded() === next) rerender();
      });
    }
  };

  const holdingKeyFromRow = (row: HTMLElement): string | null => {
    const fromAttr = String(row.dataset.holdingKey || "").trim();
    if (fromAttr) return fromAttr;
    const ticker = String(row.dataset.ticker || "").trim();
    if (!ticker) return null;
    return holdingRowKey(row.dataset.accountId ?? "", ticker);
  };

  panel.addEventListener("click", (event) => {
    if (event.detail > 1) return;
    const target = event.target as HTMLElement;
    if (target.closest(".sfe-focus-row-menu, #sfe-focus-menu-portal, .recs-ticker")) return;
    const row = target.closest<HTMLElement>("tr.sfe-stocks-row-clickable");
    const key = row ? holdingKeyFromRow(row) : null;
    if (!key) return;
    event.preventDefault();
    toggleHolding(key);
  });

  panel.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const target = event.target as HTMLElement;
    if (target.closest(".sfe-focus-row-menu, .sfe-focus-menu-btn")) return;
    const row = target.closest<HTMLElement>("tr.sfe-stocks-row-clickable");
    const key = row ? holdingKeyFromRow(row) : null;
    if (!key) return;
    event.preventDefault();
    toggleHolding(key);
  });

  document.addEventListener(STOCK_HISTORY_OPEN_EVENT, (event) => {
    if (!(event instanceof CustomEvent)) return;
    const ticker = (event.detail as { ticker?: string } | undefined)?.ticker?.trim();
    if (!ticker) return;
    const match = panel.querySelector<HTMLElement>(
      `tr.sfe-stocks-row[data-ticker="${CSS.escape(ticker)}"]`,
    );
    const key = match ? holdingKeyFromRow(match) : null;
    if (!key) return;
    toggleHolding(key);
  });
}

function mountPanel(
  panel: HTMLElement,
  state: StocksFilterState,
  cache: StocksPanelCache,
  stale = false,
): void {
  let showStale = stale;
  let liveCache = cache;
  let shareHistoryReady = Boolean(
    shareHistoryTxns && Date.now() - shareHistoryTxnsAt < TXN_CACHE_MS,
  );

  const rerender = (): void => {
    stocksRerender = rerender;
    withPreservedInputFocus(() => {
      liveCache = panelCache ?? liveCache;
      const columns = resolveWebTableColumns(TABLE_ID);
      const filtered = applyFilters(liveCache.rows, state);
      shareHistoryReady = Boolean(
        shareHistoryTxns && Date.now() - shareHistoryTxnsAt < TXN_CACHE_MS,
      );
      panel.innerHTML = renderPanelShell(
        state,
        liveCache,
        filtered,
        columns,
        expandedTicker,
        shareHistoryReady,
        showStale,
      );
      showStale = false;
      bindFilters(panel, state, rerender);
      bindDetailsCollapsiblePersistence(panel, "web-stocks");
      bindShareHistory(
        panel,
        () => expandedTicker,
        (ticker) => {
          expandedTicker = ticker;
        },
        rerender,
      );
      const resetBtn = panel.querySelector<HTMLElement>("#wheelStockResetFilters");
      if (resetBtn) {
        ensureColumnPickerBesideReset(resetBtn, {
          tableId: TABLE_ID,
          title: "Configure Stocks columns",
          defaults: getTableColumnDefaults(TABLE_ID),
          onApply: () => rerender(),
        });
      }
      syncFocusRowMenuAfterDomUpdate();
    });
  };
  rerender();
}

export async function renderNativeStocksPanel(
  panel: HTMLElement,
  opts: { force?: boolean } = {},
): Promise<void> {
  const force = opts.force ?? false;
  const state = loadFilters();
  mountedPanel = panel;

  const hasCached = Boolean(panelCache);
  if (!hasCached && !panelHasDisplayedContent(panel)) {
    panel.innerHTML = `<div class="sfe-web-panel-loading">Loading stock positions…</div>`;
  }

  try {
    if (hasCached && !force) {
      if (panelHasDisplayedContent(panel)) softUpdatePanel(panel, state, panelCache!);
      else mountPanel(panel, state, panelCache!);
      if (Date.now() - panelCache!.fetchedAt >= CACHE_MS) {
        void loadPanelCache(false).then((cache) => {
          if (mountedPanel !== panel) return;
          if (panelHasDisplayedContent(panel)) softUpdatePanel(panel, state, cache);
          else mountPanel(panel, state, cache);
        });
      }
      return;
    }

    if (panelCache && panelHasDisplayedContent(panel)) {
      try {
        const cache = await loadPanelCache(force);
        if (mountedPanel !== panel) return;
        softUpdatePanel(panel, state, cache);
        return;
      } catch {
        return;
      }
    }

    if (panelCache) {
      mountPanel(panel, state, panelCache, true);
    }

    const cache = await loadPanelCache(force);
    if (mountedPanel !== panel) return;
    mountPanel(panel, state, cache);
  } catch (err) {
    if (mountedPanel !== panel) return;
    paintPanelLoadError(panel, err, {
      title: "Stocks · long positions &amp; performance",
      view: "stocks",
      fallback: "Could not load stock positions from own ledger.",
    });
  }
}

export async function refreshNativeStocksPanelInBackground(panel: HTMLElement): Promise<void> {
  mountedPanel = panel;
  invalidateWheelSnapshots();
  const state = loadFilters();
  if (panelCache && panelHasDisplayedContent(panel)) {
    try {
      const cache = await loadPanelCache(true);
      if (mountedPanel === panel) softUpdatePanel(panel, state, cache);
    } catch {
      /* keep showing current rows */
    }
    return;
  }
  await renderNativeStocksPanel(panel, { force: true });
}

export function invalidateStocksPanelCache(): void {
  panelCache = null;
  panelInflight = null;
  invalidateWheelSnapshots();
}
